import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useEffect, useMemo } from 'react'
import { useUserState } from '~/atoms/UserState'
import {
  useGetContact,
  useGetContacts,
  useGetContactSources,
} from '~/queries/contactsQueries'
import { SearchValue } from '~/types/Shared'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import { getYearsSince } from '@mangovoice/core'
import { toNounCasing } from '@mangovoice/core'
import {
  Contact,
  ContactSource,
  ContactSourceType,
  PaginatedDataState,
  ResourceLoadingDisposition,
  ResourceLoadingState,
} from '@mangovoice/types'

export type ContactActions = 'call' | 'message' | 'fax'
const contactNumberLabels = ['fax', 'home', 'mobile', 'work', 'other'] as const
export type ContactNumberLabel = (typeof contactNumberLabels)[number]
const labelToActionMap: Record<ContactNumberLabel, ContactActions[]> = {
  fax: ['call', 'fax', 'message'],
  home: ['call', 'message'],
  mobile: ['call', 'message'],
  work: ['call', 'message'],
  other: ['call', 'message'],
}
export interface ContactNumber {
  id: number
  label: ContactNumberLabel
  number: string
  isPrimary?: boolean
  isExtension?: boolean
  availableActions: ContactActions[]
  availableQuickActions: ContactActions[]
}

export type ContactHistoryType =
  | 'messaging'
  | 'inboundCall'
  | 'outboundCall'
  | 'inboundFax'
  | 'outboundFax'
  | 'voicemail'

export interface ContactHistory {
  id: number
  date: Date
  type: ContactHistoryType
  fromNumber?: string | ContactNumber
  toNumber?: string | ContactNumber
}
export type ContactDetails =
  | {
      id: number
      name: string
      age?: number
      gender?: string
      numbers: ContactNumber[]
      defaultNumber: ContactNumber
      history?: ContactHistory[]
      source: ContactSourceType
      sourceName: string
    }
  | {
      id: number
      name: string
      age?: number
      gender?: string
      numbers: undefined
      defaultNumber: undefined
      history?: ContactHistory[]
      source: ContactSourceType
      sourceName: string
    }

const CONTACTS_PAGE_SIZE = 100
const CONTACT_SOURCES_PAGE_SIZE = 999
export interface ContactsViewModelState {
  selectedContactId?: number | string
  contactsListState: PaginatedDataState<Contact>
  contactsListLoadingState?: ResourceLoadingDisposition
  contactSources?: ContactSource[]
  contactSourcesLoadingState?: ResourceLoadingDisposition
  selectedContactSourceId?: number
  selectedContactInstance?: Contact
  selectedContactDetails?: ContactDetails
  selectedContactLoadingState?: ResourceLoadingDisposition
  searchValue?: SearchValue
}

export interface ContactsLocalStorageState {
  selectedContactSourceId?: number
}

let rootContactsViewModelState: ContactsViewModelState = {
  contactsListState: {
    data: [],
    hasNextPage: false,
    nextPageOffset: 0,
  },
}
const initialStateAtom = atom<ContactsViewModelState>(
  rootContactsViewModelState,
)
//getOnInit makes the atom read localStorage on first render; without it the
//value only hydrates in an effect, after the mount effect below has already run
const contactsLocalStorageAtom = atomWithStorage<ContactsLocalStorageState>(
  'contacts-ls',
  {},
  undefined,
  { getOnInit: true },
)
export const useContactsViewModel = () => {
  const { pbxId } = useUserState()
  const [state, __setState] = useAtom(initialStateAtom)
  const [ls, __setLs] = useAtom(contactsLocalStorageAtom)

  const setState = (newState: ContactsViewModelState) => {
    const updated = { ...newState }
    rootContactsViewModelState = updated
    __setState(updated)
    __setLs({
      selectedContactSourceId: updated.selectedContactSourceId,
    })
  }

  const appendState = (newState: Partial<ContactsViewModelState>) => {
    const updated = { ...rootContactsViewModelState, ...newState }
    setState(updated)

    return updated
  }

  //on mount, set the selected contact source id from local storage
  useEffect(() => {
    if (ls.selectedContactSourceId) {
      appendState({
        selectedContactSourceId: ls.selectedContactSourceId,
      })
    }
  }, [])

  //#region Queries
  const { refetch: getContactSourcesInternal } = useGetContactSources({
    pbxId: pbxId ?? 0,
    onQuerySuccess: (paginatedResult) => {
      appendState({
        contactSources: paginatedResult.results,
        contactSourcesLoadingState: ResourceLoadingDisposition.LoadSuccess,
      })
    },
    onQueryError: (_, error) => {
      appendState({
        contactSourcesLoadingState:
          ResourceLoadingDisposition.FromApiError(error),
      })
    },
  })

  const {
    refetch: getContactsInternal,
    tryCancelQuery: cancelGetContactsInternal,
  } = useGetContacts({
    pbxId: pbxId ?? 0,
    onQuerySuccess: (paginatedResult) => {
      const paginationParts = getPaginatedResultParts(paginatedResult)
      if (!paginationParts) {
        //something went wrong and we're actually in an error state
        appendState({
          contactsListLoadingState: {
            state: ResourceLoadingState.LoadError,
          },
        })
        return
      }

      const currentData = rootContactsViewModelState.contactsListState.data
      const appended = [...currentData, ...paginationParts.results]

      appendState({
        contactsListState: {
          data: appended,
          hasNextPage: paginationParts.hasNextPage,
          nextPageOffset: paginationParts.nextPageOffset,
        },
        contactsListLoadingState: ResourceLoadingDisposition.LoadSuccess,
      })
    },
    onQueryError: (error, _) => {
      appendState({
        contactsListLoadingState:
          ResourceLoadingDisposition.FromApiError(error),
      })
    },
  })

  const { refetch: getContactInternal } = useGetContact({
    pbxId: pbxId,
    onQuerySuccess: (contact) => {
      appendState({
        selectedContactInstance: contact,
        selectedContactLoadingState: ResourceLoadingDisposition.LoadSuccess,
      })
    },
    onQueryError: (_, error) => {
      appendState({
        selectedContactInstance: undefined,
        selectedContactLoadingState:
          ResourceLoadingDisposition.FromApiError(error),
      })
    },
  })
  //#endregion

  //#region Actions
  const getContactSources = () => {
    appendState({
      contactSourcesLoadingState: ResourceLoadingDisposition.Loading,
    })
    getContactSourcesInternal({ offset: 0, limit: CONTACT_SOURCES_PAGE_SIZE })
  }

  const getContacts = (offset: number = 0, resetContactsListState = false) => {
    if (resetContactsListState) {
      appendState({
        contactsListState: {
          data: [],
          hasNextPage: false,
          nextPageOffset: 0,
        },
      })
    }

    appendState({
      contactsListLoadingState: ResourceLoadingDisposition.Loading,
    })

    getContactsInternal({
      offset,
      contactSourceIds: rootContactsViewModelState?.selectedContactSourceId
        ? [rootContactsViewModelState?.selectedContactSourceId]
        : undefined,
      ordering: ['given_name', 'family_name'],
      search: rootContactsViewModelState.searchValue?.value,
      limit: CONTACTS_PAGE_SIZE,
    })

    return cancelGetContactsInternal
  }

  const getNextContactsPage = () => {
    if (
      !rootContactsViewModelState.contactsListState.hasNextPage ||
      rootContactsViewModelState.contactsListLoadingState ===
        ResourceLoadingDisposition.Loading
    ) {
      return
    }

    appendState({
      contactsListLoadingState: ResourceLoadingDisposition.Loading,
    })

    return getContacts(
      rootContactsViewModelState.contactsListState.nextPageOffset,
    )
  }

  const getContact = (contactId: number | string) => {
    appendState({
      selectedContactLoadingState: ResourceLoadingDisposition.Loading,
    })
    return getContactInternal(contactId)
  }

  const setSelectedContactSource = (
    contactSource?: ContactSource,
    requeryContacts = true,
  ) => {
    appendState({
      selectedContactSourceId: contactSource?.id,
    })

    if (!requeryContacts) {
      return
    }

    return getContacts(0, true)
  }

  const setSelectedContactSourceId = (
    contactSourceId?: number,
    requeryContacts = true,
  ) => {
    const contactSource = rootContactsViewModelState.contactSources?.find(
      (cs) => cs.id === contactSourceId,
    )
    setSelectedContactSource(contactSource, requeryContacts)
  }

  const setSelectedContactId = (
    contactId?: number | string,
    queryContactInstance = true,
  ) => {
    appendState({
      selectedContactId: contactId,
    })

    if (!queryContactInstance || !contactId) {
      return
    }

    getContact(contactId)
  }

  const setSearchValue = (searchValue: SearchValue, requeryContacts = true) => {
    appendState({
      searchValue,
    })

    if (!requeryContacts) {
      return
    }

    return getContacts(0, true)
  }

  const resetState = () => {
    setState({
      contactsListState: {
        data: [],
        hasNextPage: false,
        nextPageOffset: 0,
      },
    })
  }

  const getContactDetails = (contact: Contact): ContactDetails => {
    const contactSource = state.contactSources?.find(
      (source) => source.id === contact.contact_source_id,
    )

    let tempContactDetails: ContactDetails = {
      id: contact.id,
      name: `${contact.given_name} ${contact.family_name}`,
      source: contactSource?.source ?? ContactSourceType.Pbxes,
      sourceName: contactSource?.name ?? 'Unknown Source',
      gender: contact.gender ? toNounCasing(contact.gender) : undefined,
      age: getYearsSince(contact.date_of_birth),
      defaultNumber: undefined,
      numbers: undefined,
    }

    const numbers: ContactNumber[] | undefined = contact.phone_numbers
      ?.filter((number) => number?.number !== undefined)
      .map((number) => {
        let contactNumberLabel: ContactNumberLabel
        if (contactNumberLabels.find((label) => label === number.label)) {
          contactNumberLabel = number.label as ContactNumberLabel
        } else {
          contactNumberLabel = 'other'
        }
        const isExtension =
          contactSource?.source === ContactSourceType.Extensions
        let availableQuickActions = labelToActionMap[contactNumberLabel]
        let availableActions: ContactActions[] = [
          ...availableQuickActions,
          'fax',
        ]
        if (isExtension) {
          availableQuickActions = ['call']
          availableActions = ['call']
        }
        return {
          id: number.id,
          label: contactNumberLabel,
          number: number.number!,
          isPrimary: number.id === contact.primary_phone_number?.id,
          isExtension,
          availableQuickActions: availableQuickActions,
          availableActions: availableActions,
        }
      })

    if (numbers && numbers.length > 0) {
      const defaultNumber =
        numbers.find((number) => number.isPrimary) ??
        numbers.find((number) => number.label === 'mobile') ??
        numbers.find((number) => number.label === 'home') ??
        numbers[0]

      tempContactDetails = {
        ...tempContactDetails,
        numbers,
        defaultNumber,
      }
    }
    return { ...tempContactDetails }
  }
  const selectedContactDetails: ContactDetails | undefined = useMemo(() => {
    if (state.selectedContactInstance) {
      return getContactDetails(state.selectedContactInstance)
    }
    return undefined
  }, [state.selectedContactInstance])

  //#endregion

  return {
    state: {
      ...state,
      selectedContactDetails,
    } as ContactsViewModelState,
    actions: {
      getContactDetails,
      getContactSources,
      getContacts,
      getNextContactsPage,
      getContact,
      setSelectedContactSource,
      setSelectedContactSourceId,
      setSelectedContactId,
      setSearchValue,
      resetState,
    },
  }
}
